import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, BellRing, CreditCard } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const stripePromise = process.env.REACT_APP_STRIPE_PK ? loadStripe(process.env.REACT_APP_STRIPE_PK) : null;

function SaveCardForm({ wlId, onSaved }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    const { error } = await stripe.confirmSetup({ elements, redirect: 'if_required' });
    if (error) { toast.error(error.message || 'Card could not be saved'); setBusy(false); return; }
    try { await axios.post(`${API}/waitlist/${wlId}/card-saved`); } catch { /* verified later */ }
    onSaved();
  };
  return (
    <div className="space-y-3">
      <PaymentElement />
      <Button onClick={save} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="waitlist-save-card">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-1.5" />}
        Save card & hold my place
      </Button>
      <p className="text-[10px] text-muted-foreground">Your card is only charged if you're released a ticket. You can leave the list anytime.</p>
    </div>
  );
}

export function WaitlistSignup({ eventId, orgSlug, compact = false }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState(null);
  const [cardDone, setCardDone] = useState(false);
  const [waitingCount, setWaitingCount] = useState(0);
  const [info, setInfo] = useState({ closed: false, message: '' });

  useEffect(() => {
    axios.get(`${API}/events/${eventId}/waitlist/info`).then(r => {
      setWaitingCount(r.data.total_waiting || 0);
      setInfo({ closed: !!r.data.closed, message: r.data.message || '' });
    }).catch(() => {});
  }, [eventId]);

  const join = async () => {
    if (!name.trim() || !email.includes('@')) return toast.error('Name and a valid email required');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/events/${eventId}/waitlist/join`, { name, email, qty });
      setJoined(r.data);
      if (r.data.result === 'already_on_list' && r.data.card_saved) setCardDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not join'); }
    finally { setBusy(false); }
  };

  if (joined) {
    return (
      <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 space-y-2" data-testid="waitlist-joined">
        <p className="font-semibold text-amber-300 flex items-center gap-1.5"><BellRing className="h-4 w-4" /> You're #{joined.position} in line {joined.total_waiting ? `of ${joined.total_waiting}` : ''}</p>
        {!cardDone && joined.client_secret && stripePromise ? (
          <Elements stripe={stripePromise} options={{ clientSecret: joined.client_secret, appearance: { theme: 'night' } }}>
            <SaveCardForm wlId={joined.wl_id} onSaved={() => { setCardDone(true); toast.success('Card saved — you\'ll be charged only when your ticket is released'); }} />
          </Elements>
        ) : (
          <p className="text-xs text-muted-foreground">✓ {cardDone ? 'Card on file — the moment tickets are released, yours is charged and your QR ticket lands in your email automatically.' : "We'll email you when tickets are released."}</p>
        )}
        <a href={joined.status_url} className="text-xs text-indigo-400 hover:underline block" data-testid="waitlist-status-link">Track my place in line →</a>
      </div>
    );
  }

  if (info.closed) {
    return (
      <div className={`rounded-xl border p-4 space-y-1 ${compact ? 'border-slate-300 bg-slate-50' : 'border-border bg-muted/20'}`} data-testid="waitlist-closed">
        <p className={`font-semibold ${compact ? 'text-slate-700' : 'text-muted-foreground'}`}>The waitlist for this event is closed.</p>
        {info.message && <p className={`text-xs ${compact ? 'text-amber-700' : 'text-amber-300'}`} data-testid="waitlist-org-message">💬 {info.message}</p>}
        {orgSlug && <p className={`text-[10px] ${compact ? 'text-slate-500' : 'text-muted-foreground'}`}><a href={`/org/${orgSlug}`} className="text-indigo-500 hover:underline">Follow the organizer</a> to hear about the next event.</p>}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 space-y-2.5" data-testid="waitlist-signup">
      <div>
        <p className={`font-semibold flex items-center gap-1.5 ${compact ? 'text-amber-700' : 'text-amber-300'}`}><BellRing className="h-4 w-4" /> Sold out — join the waitlist</p>
        {info.message && <p className={`text-xs mt-1 ${compact ? 'text-amber-700' : 'text-amber-200'}`} data-testid="waitlist-org-message">💬 {info.message}</p>}
        <p className="text-xs text-muted-foreground mt-0.5">
          Save your card and the second tickets open up you're auto-charged, in line order — your QR ticket arrives instantly.
          {waitingCount > 0 && ` ${waitingCount} ${waitingCount === 1 ? 'person is' : 'people are'} in line.`}
        </p>
      </div>
      <div className={compact ? 'space-y-2' : 'grid grid-cols-2 gap-2'}>
        <Input placeholder="Your name" value={name} onChange={e => setName(e.target.value)} data-testid="waitlist-name" />
        <Input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} data-testid="waitlist-email" />
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Tickets wanted</span>
        <select className="h-8 rounded-md border border-border bg-background text-foreground px-2 text-sm" style={{ colorScheme: 'dark' }} value={qty} onChange={e => setQty(parseInt(e.target.value, 10))} data-testid="waitlist-qty">
          {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <Button onClick={join} disabled={busy} className="ml-auto bg-amber-600 hover:bg-amber-700 text-white" data-testid="waitlist-join-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Join waitlist'}
        </Button>
      </div>
      {orgSlug && <p className="text-[10px] text-muted-foreground">Or <a href={`/org/${orgSlug}`} className="text-indigo-400 hover:underline">follow the organizer</a> to hear about new dates and releases.</p>}
    </div>
  );
}
